import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBrand } from "../../contexts/BrandContext";
import { formatErr } from "../../lib/api";
import { PasswordField } from "../../components/PasswordField";
import { BrandLogoCircle } from "../../components/BrandLogoCircle";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { MfaStep } from "../../components/MfaStep";

/**
 * Login centrale per PLATFORM_ADMIN. Riusa `/api/auth/login`.
 *   - se `platform_admin=false` → deny (accesso centrale negato).
 *   - se `must_change_password=true` → /platform/change-password.
 *   - else → /platform/salon-central.
 */
export default function PlatformLogin() {
  const { login, completeMfa } = useAuth();
  const { brand } = useBrand();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);

  const route = (u) => {
    if (u.role === "AGENT") {
      toast.success(`Benvenuto in ${brand.display_name}`);
      nav(from && from.startsWith("/agent") ? from : "/agent/dashboard");
      return;
    }
    if (!u.platform_admin) { toast.error("Accesso riservato al PLATFORM_ADMIN."); return; }
    const safeFrom = from && from.startsWith("/platform") && !from.startsWith("/platform/login") && !from.startsWith("/platform/change-password");
    toast.success(`Benvenuto in ${brand.display_name}`);
    nav(u.must_change_password ? "/platform/change-password" : (safeFrom ? from : "/platform/crm/dashboard"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pw = password;
      const data = await login(email, password);
      setPassword("");
      if (data.mfa_required === "verify") setMfa({ stage: "verify" });
      else if (data.mfa_offer) { await completeMfa(); setMfa({ stage: "enroll", pw }); }
      else await mfaDone();
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  const mfaDone = async (recoveryLeft) => {
    const u = await completeMfa();
    if (!u) return toast.error("Accesso non riuscito: riprova.");
    if (recoveryLeft !== undefined && recoveryLeft <= 3) toast.warning(`Ti restano ${recoveryLeft} codici di recupero: rigenerali dal profilo.`);
    route(u);
  };

  return (
    <div data-testid="platform-login-page" className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-dark) 0%, #2d1526 100%)", color: "white" }}>
      {mfa ? (
        <div className="w-full max-w-md p-6 sm:p-8 rounded-2xl border border-white/10 backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.04)" }}>
          <MfaStep stage={mfa.stage} email={email} password={mfa.pw} onDone={mfaDone} onCancel={() => setMfa(null)} />
        </div>
      ) : (
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-2xl border border-white/10 backdrop-blur-sm"
        style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="flex flex-col items-center text-center mb-6">
          <BrandLogoCircle className="mb-5 h-36 w-36 sm:h-40 sm:w-40" boxTestId="platform-login-logo-box" imgTestId="platform-login-logo" />
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4" style={{ color: "var(--brand-accent)" }} />
            <span className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--brand-accent)" }}>
              Console centrale
            </span>
          </div>
          <h1 className="font-serif text-3xl" data-testid="platform-login-title">{brand.display_name}</h1>
        </div>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Email</span>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
            data-testid="platform-login-email" autoComplete="username"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none"
            style={{ borderColor: undefined }}
            onFocus={e => (e.target.style.borderColor = "var(--brand-accent)")}
            onBlur={e => (e.target.style.borderColor = "")} />
        </label>
        <label className="block mb-6">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Password</span>
          <PasswordField required value={password} onChange={e => setPassword(e.target.value)}
            data-testid="platform-login-password" autoComplete="current-password"
            iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none"
            onFocus={e => (e.target.style.borderColor = "var(--brand-accent)")}
            onBlur={e => (e.target.style.borderColor = "")} />
        </label>
        <button type="submit" disabled={busy} data-testid="platform-login-submit"
          className="w-full py-3 rounded-lg disabled:opacity-50 uppercase tracking-widest text-sm font-medium transition-colors"
          style={{ background: "var(--brand-primary)", color: "white" }}>
          {busy ? <Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> : null}
          Accedi
        </button>
        <Link to="/agent/register" data-testid="platform-agent-register-link"
          className="mt-3 w-full inline-flex items-center justify-center py-3 rounded-lg border-2 uppercase tracking-widest text-sm font-medium transition-colors hover:bg-white/10"
          style={{ borderColor: "var(--brand-accent)", color: "var(--brand-accent)" }}>
          Registrati come agente
        </Link>
        <div className="mt-5 text-center">
          <Link to="/platform/forgot-password" data-testid="platform-forgot-link"
            className="text-xs text-white/70 hover:text-white underline">
            Password dimenticata?
          </Link>
        </div>
      </form>
      )}
    </div>
  );
}
